import { useAppSelector } from "@app/hooks";
import TransformedImage from "@components/TransformedImage";
import SurveyPDFPage from "@survey-export/SurveyPDFPage";
import PDFPreview from "@survey-preview/PDFPreview";
import {
  selectCurrentSurvey,
  selectPropertyMedia,
} from "@survey/surveySelectors";

interface FloorPlanImageAtIndexProps {
  propertyId: string;
  imageIndex: number;
}

/**
 * Component to render a specific floor plan image at given index
 * Used for URL-based floor plan preview functionality
 */
const FloorPlanImageAtIndex = ({
  propertyId,
  imageIndex,
}: FloorPlanImageAtIndexProps) => {
  // Use store-first architecture with selectors
  const survey = useAppSelector(selectCurrentSurvey);
  const propertyMedia = useAppSelector(selectPropertyMedia(propertyId));

  if (!survey?.isIncludeSitePlans) {
    return (
      <div className="p-4 text-center text-gray-500">
        Site plans are not included in this survey
      </div>
    );
  }

  if (!propertyMedia) {
    return (
      <div className="p-4 text-center text-gray-500">Loading media data...</div>
    );
  }

  if (!propertyMedia?.floorPlans) {
    return (
      <div className="p-4 text-center text-gray-500">
        No floor plans available for this property
      </div>
    );
  }

  const visibleFloorPlans = propertyMedia.floorPlans.filter(
    (plan) => !plan.isHidden && plan.cloudfrontUrl,
  );

  if (imageIndex < 0 || imageIndex > visibleFloorPlans.length) {
    return (
      <div className="p-4 text-center text-gray-500">
        Floor plan at index {imageIndex} not found. Available: 0-
        {visibleFloorPlans.length - 1}
      </div>
    );
  }

  const floorPlan = visibleFloorPlans[imageIndex - 1];
  const isPdf = floorPlan.displayName?.toLowerCase().includes(".pdf") || false;

  if (isPdf) {
    // Use PDFPreview component for PDF files like PropertySitePlanPreview does
    return (
      <PDFPreview
        items={[
          {
            cloudfrontUrl: floorPlan.cloudfrontUrl!,
            thumbnailUrl: floorPlan.thumbnailUrl,
            mediaId: floorPlan.mediaId,
          },
        ]}
        pdfPaperSize={survey?.pdfPaperSize}
      />
    );
  } else {
    // Use SurveyPDFPage for image files like PropertySitePlanPreview does
    return (
      <SurveyPDFPage mode="SitePlanPreview">
        <div className="p-4 w-full">
          <TransformedImage
            imageUrl={floorPlan.cloudfrontUrl!}
            alt={`Floor Plan ${imageIndex + 1}`}
            className="max-w-[95%] w-full h-full bg-contain"
            showFallback={true}
            asBackground={true}
          />
        </div>
      </SurveyPDFPage>
    );
  }
};

export default FloorPlanImageAtIndex;
